let _ = require('../_.js');
var $ = jQuery;

let _debug_data = false;

let model_cache_public = {};
let model_cache_private = {};

let active_requests = {};

// ============================================================================
// BATCH REQUEST SYSTEM - Intercepts individual AJAX calls and groups them
// ============================================================================

// Batch completion callbacks for UI spinners
let batch_completion_callbacks = [];
_.onBatchComplete = function (callback) {
    batch_completion_callbacks.push(callback);
};

// Batch queues - one per endpoint type, 50ms debounce
// NOTE: Public requests (frontend/public, backend/restricted with revision) are NOT batched
// because batching would prevent the proxy server from caching individual responses.
// Public requests with revision numbers need individual URLs for effective CDN/proxy caching.
let batch_queues = {
    'backend_restricted': { requests: [], timeout: null },
    'backend_private': { requests: [], timeout: null },
    'frontend_private': { requests: [], timeout: null }
};

const BATCH_DELAY_MS = 50; // 50ms debounce to accumulate simultaneous requests
const BATCH_MAX_SIZE = 20; // Immediately flush when batch reaches this size

// _.ajax_batched() - Drop-in replacement for _.ajax_retryable() that batches requests
// Returns a Promise that resolves with the same format as individual requests
_.ajax_batched = async function (ajax_options) {
    // Detect if this is a batchable request
    let batch_type = null;

    if (ajax_options.url && ajax_options.url.includes('/model/backend/restricted')) {
        batch_type = 'backend_restricted';
    } else if (ajax_options.url && ajax_options.url.includes('/model/backend/private')) {
        batch_type = 'backend_private';
    } else if (ajax_options.url && ajax_options.url.includes('/model/frontend/private')) {
        batch_type = 'frontend_private';
    }

    // If not batchable, pass through to regular ajax
    if (!batch_type) {
        return await _.ajax_retryable(ajax_options);
    }

    // Extract model and id from the request
    let model = ajax_options.data.model;
    let id = ajax_options.data.id;
    let revision = ajax_options.data.revision || null;

    // Return a Promise that will be resolved when the batch completes
    return new Promise((resolve, reject) => {
        let queue = batch_queues[batch_type];

        // Add this request to the queue
        queue.requests.push({
            model: model,
            id: id,
            revision: revision,
            ajax_options: ajax_options,
            resolve: resolve,
            reject: reject
        });

        // If batch size reaches max, flush immediately
        if (queue.requests.length >= BATCH_MAX_SIZE) {
            // Clear any pending timeout
            if (queue.timeout) {
                clearTimeout(queue.timeout);
                queue.timeout = null;
            }
            // Flush immediately (don't await - let it run async)
            flush_batch_queue(batch_type);
        } else {
            // Reset the debounce timer for smaller batches
            if (queue.timeout) {
                clearTimeout(queue.timeout);
            }

            // Set timer to flush after BATCH_DELAY_MS
            queue.timeout = setTimeout(() => {
                flush_batch_queue(batch_type);
            }, BATCH_DELAY_MS);
        }
    });
};

// Flush a batch queue - sends batched request and unpacks responses
async function flush_batch_queue(batch_type) {
    let queue = batch_queues[batch_type];

    if (!queue || queue.requests.length === 0) {
        return;
    }

    // Clear timeout
    if (queue.timeout) {
        clearTimeout(queue.timeout);
        queue.timeout = null;
    }

    // Take all queued requests
    let requests_to_process = queue.requests;
    queue.requests = [];

    // Deduplicate: keep highest revision for each model:id
    let deduped = {};
    for (let req of requests_to_process) {
        let key = req.model + ':' + req.id;
        if (!deduped[key] || (req.revision && req.revision > deduped[key].revision)) {
            deduped[key] = req;
        }
    }

    let unique_requests = Object.values(deduped);

    // Map batch type to endpoint
    let batch_url_map = {
        'backend_restricted': '/api/ext_v1/model/batch_restricted',
        'backend_private': '/api/ext_v1/model/batch_restricted',
        'frontend_private': '/api/ext_v1/model/batch_private'
    };

    let batch_url = batch_url_map[batch_type];

    try {
        // Build batch request payload
        let batch_payload = {
            requests: JSON.stringify(unique_requests.map(r => ({
                model: r.model,
                id: r.id
            }))),
            deployment_key: _.get_deployment_key()
        };

        // Determine if we need bearer token
        let ajax_config = {
            url: _.ajax_url_prefix ? _.ajax_url_prefix + batch_url : batch_url,
            method: 'POST',
            data: batch_payload,
            dataType: "json"
        };

        // Add auth header for frontend private requests
        if (batch_type === 'frontend_private' && _.AuctionUI_Session && _.AuctionUI_Session.get_token_cookie()) {
            ajax_config.beforeSend = function (xhr) {
                xhr.setRequestHeader('Authorization', 'Bearer ' + _.AuctionUI_Session.get_token_cookie());
            };
        }

        // Make the batch request
        let batch_response = await new Promise((resolve, reject) => {
            ajax_config.success = function (data) { resolve(data); };
            ajax_config.error = function (jqXHR, textStatus, errorThrown) {
                reject(new Error(`Batch ${batch_type} failed: ${textStatus}`));
            };
            $.ajax(ajax_config);
        });

        // Unpack batch response and resolve individual promises
        if (batch_response && batch_response.success && batch_response.records) {
            // For each request, find its response and resolve its promise
            for (let req of requests_to_process) {
                let key = req.model + ':' + req.id;
                let record_data = batch_response.records[key];

                if (record_data) {
                    // Determine scope based on batch type
                    let scope = (batch_type === 'backend_private' || batch_type === 'frontend_private') ? 'private' : 'public';

                    // Format response exactly like individual AJAX requests
                    // Format: [model, id, revision, data, scope]
                    let formatted_response = [
                        req.model,
                        req.id,
                        record_data._revision,
                        record_data,
                        scope
                    ];

                    // Resolve this request's promise
                    req.resolve(formatted_response);
                } else {
                    // Record not in response - resolve with null
                    req.resolve(null);
                }
            }

            // Call batch completion callbacks for UI
            for (let callback of batch_completion_callbacks) {
                try {
                    callback(batch_type, Object.keys(batch_response.records).length);
                } catch (e) {
                    console.error("Batch completion callback error:", e);
                }
            }
        } else {
            throw new Error("Batch response invalid");
        }

    } catch (error) {
        console.error(`Batch ${batch_type} error, falling back to individual requests:`, error);

        // Fallback: make individual requests
        for (let req of requests_to_process) {
            try {
                let result = await _.ajax_retryable(req.ajax_options);
                req.resolve(result);
            } catch (err) {
                req.reject(err);
            }
        }
    }
}

// Fetches a record from the server and returns an orm _.Model_Record object populated with the data.

// The passed revision number isn't to get an actual revision of a record, but to indicate the revision
// number we are expecting to get.  If requesting a record by revision, the response will either be a
// cached version of the record at this revision number, or the latest version of the record if the
// record is not at this revision number.  The expectation is that the websocket server will tell us the
// correct revision number as soon as it learns it, and update the record with the result as soon as it
// is updated.  The revision number is just a hint of the expected revision to allow for being able
// to accessed cached data.
_.get_record = function (model, id, revision = null, scope = 'public', force_reload = false) {
    return __get_record({
        model: model,
        id: id,
        revision: revision,
        scope: scope
    }, force_reload);
};

// Refreshes every cached record with the latest copy from the server. Heavy: use it when the view of
// everything changes (e.g. a bidder logged in), not to pick up one record's change.
_.data_sync = async function (model = null) {
    let promises = [];

    for (let cache of [model_cache_public, model_cache_private]) {
        for (let k in cache) {
            if (cache[k] && cache[k].refresh) {
                promises.push(cache[k].refresh());
            }
        }
    }

    await Promise.allSettled(promises); // a failed refetch must not stop the others
}

// The WS server restarted (core/websocket.js): updates posted while it was down never reached us
// and its catch-up cache is empty, so every held record is refetched. Without a revision hint: a
// by-revision response is cached (browser, ModelProxy) and would be the stale copy. Spread over a
// few seconds so a restart does not land every viewer's refetch on PHP in the same second.
_.on_websocket_server_restarted(async function () {
    await _.sleep(Math.random() * 5000);
    let promises = [];
    for (let cache of [model_cache_public, model_cache_private]) {
        for (let k in cache) {
            let record = cache[k];
            if (record) {
                promises.push(__get_record({ model: record._model, id: record.id, revision: null, scope: record._scope, resync: true }, true));
            }
        }
    }
    await Promise.allSettled(promises); // a failed refetch must not stop the others
});



// Private version of get_record.  You probably want to call get_record() directly.
async function __get_record(request, force_reload = false) {
    return new Promise(async function (resolve, reject) {

        let model = request.model;
        let id = request.id;
        let revision = request.revision;
        let scope = request.scope;

        // if (model == 'App\\Models\\Registrant' && id == 5) {
        // console.trace();
        // }

        // Maybe make these constants?
        if (scope != 'private') {
            scope = 'public';
        }

        // Private requests by non logged in users are just public requests
        if (_.is_frontend && !(_.AuctionUI_Session && _.AuctionUI_Session.get_token_cookie())) {
            scope = 'public';
        }

        let request_key = _get_request_key(model, id, revision, scope);
        let cache_key = _get_cache_key(model, id);

        let _cache = model_cache_public;

        if (scope == 'private') {
            _cache = model_cache_private;
        }

        if (_cache[cache_key] === null && revision) {
            resolve(null);
            return;
        }

        if (_cache[cache_key] &&
            !force_reload &&
            (revision == null || revision <= _cache[cache_key]._revision)
        ) {
            resolve(_cache[cache_key]);
            return;
        }

        if (id == null) {
            console.warn("Can't request a null id", request);
            resolve(null);
            return null;
        }

        if (active_requests[request_key]) {
            active_requests[request_key].other_callbacks.push(function (record, error) {
                if (error) {
                    reject(error);
                } else {
                    resolve(record);
                }
            });
            return;
        }

        request.other_callbacks = [];
        active_requests[request_key] = request;

        let result = null;

        // A failed fetch settles every waiter with the error and frees the request key (an exception here used
        // to leave the promise pending forever, which stalled every later realtime update on the page)
        try {

        // If inline ORM data was provided via WebSocket, use it directly (no HTTP fetch needed)
        if (request.websocket_public_record) {
            result = request.websocket_public_record;
        }

        // Based on source of request and scope, we call different endpoints.  Each endpoint
        // has a different caching strategy.  In particular, a request for a specific revision
        //

        else if (scope == "public") {
            if (_.is_frontend && request.revision) {

                // NOTE: Using _.ajax_retryable() instead of _.ajax_batched() here because
                // public requests with revision numbers need individual URLs for proxy caching.
                // Batching would combine multiple requests into one endpoint, preventing the
                // proxy server from caching individual responses by their revision URLs.
                result = await _.ajax_retryable({
                    url: '/api/ext_v1/model/frontend/public_by_revision',
                    method: 'GET',
                    data: { model: request.model, id: request.id, revision: request.revision },
                    dataType: "json",
                    error: function (jqXHR, textStatus, errorThrown) {
                        console.error(jqXHR);
                    }
                });


            } else if (_.is_frontend) {
                if (!request.resync) console.warn("no rev", request);
                // NOTE: Using _.ajax_retryable() instead of _.ajax_batched() here because
                // public requests need individual URLs for proxy caching. Batching would prevent
                // the proxy server from caching individual responses effectively.
                result = await _.ajax_retryable({
                    url: '/api/ext_v1/model/frontend/public_no_revision',
                    method: 'POST',
                    data: { model: request.model, id: request.id },
                    dataType: "json",
                    error: function (jqXHR, textStatus, errorThrown) {
                        console.error(jqXHR);
                    }
                });
            } else if (_.is_backend && request.revision) {

                result = await _.ajax_batched({
                    url: '/api/ext_v1/model/backend/restricted_by_revision',
                    method: 'POST',
                    data: { model: request.model, id: request.id, revision: request.revision },
                    dataType: "json",
                    error: function (jqXHR, textStatus, errorThrown) {
                        console.error(jqXHR);
                    }
                });

            } else if (_.is_backend) {
                result = await _.ajax_batched({
                    url: '/api/ext_v1/model/backend/restricted_no_revision',
                    method: 'POST',
                    data: { model: request.model, id: request.id },
                    dataType: "json",
                    error: function (jqXHR, textStatus, errorThrown) {
                        console.error(jqXHR);
                    }
                });
            }
        } else if (scope == "private") {
            if (_.is_frontend && _.AuctionUI_Session && _.AuctionUI_Session.get_token_cookie()) {
                // Ajax request for logged in frontend users
                result = await _.ajax_batched({
                    url: '/api/ext_v1/model/frontend/private',
                    method: 'POST',
                    data: { model: request.model, id: request.id },
                    dataType: "json",
                    beforeSend: function (xhr) {
                        xhr.setRequestHeader('Authorization', 'Bearer ' + _.AuctionUI_Session.get_token_cookie());
                    },
                    error: function (jqXHR, textStatus, errorThrown) {
                        console.error(jqXHR);
                    }
                });
            } else if (_.is_backend) {
                // Ajax request for logged in backend users
                result = await _.ajax_batched({
                    url: '/api/ext_v1/model/backend/private',
                    method: 'POST',
                    data: { model: request.model, id: request.id },
                    dataType: "json",
                    error: function (jqXHR, textStatus, errorThrown) {
                        console.error(jqXHR);
                    }
                });
            }
        }

        // Hackey - this will only happen on admin side.  Shows what is loading in the clerk.
        // $("#clerk_bidding_control_container_loading_what").text(request.model.replace("App\\Models\\", "") + " (" + request.id + ")");

        } catch (error) {
            delete active_requests[request_key];
            for (let c of request.other_callbacks) {
                c(null, error);
            }
            reject(error);
            return;
        }

        delete active_requests[request_key];

        if (!result || !result[3]) {
            // Null responses can be cached locally.  They wont be cached on the proxy, however.

            // Note for later: proxy should only cache requests where the requested
            // revision is lower or equal to the actual revision.
            if (request.revision) {
                _cache[cache_key] = null;
            }

            for (let c of request.other_callbacks) {
                c(null);
            }

            resolve(null);
            return;
        }

        // Index 3 contains the record data.
        let data = result[3];

        // console.warn(data);

        // Data received.  Set the data in the data cache.
        _.__update_data_record_from_ajax_response(model, id, data, scope, _cache)

        // Return the instantiated requested record from data cache.

        for (let c of request.other_callbacks) {
            c(_cache[cache_key]);
        }

        resolve(_cache[cache_key]);
        return;
    });
}


// Updates the data record cache with updated data.  This could come from an ajax request from get_record,
// or from an ajax response from a POST request a user made.
//
// Set _cache to 'hybrid' to update both private and public caches with the passed data.  This is a bit of a hack.
// Since this fuctionality is only used by behind the scenes logic, it should be fine.
_.__update_data_record_from_ajax_response = function (model, id, data, scope, _cache = null) {

    if (scope === 'hybrid' || _cache === null) {
        _.__update_data_record_from_ajax_response(model, id, data, 'public', model_cache_public);
        _.__update_data_record_from_ajax_response(model, id, data, 'private', model_cache_private);
        return;
    }

    let cache_key = _get_cache_key(model, id);

    // Create new instantiation of target model.
    let model_class = _.get_model_class_by_name(model);

    // Setup a new associative array with relationships
    let row_new = {};

    // Copy the data from the result into the object, turning relationship records into methods to access the data
    for (let k in data) {
        if (typeof (data[k]) == 'object' && data[k] !== null) {
            row_new[k] = new _.Model_Relationship(model_class, data.id, k, data[k].model, data[k].relation, data[k].data, scope);
        } else {
            row_new[k] = data[k];
        }
    }

    // Is it in the cache already?
    if (_cache[cache_key]) {

        // The record has changed, update it in cache and returned cached record
        _cache[cache_key]._populate(row_new);

        if (_debug_data) console.log("Done Updating realtime record ", model, id, "...");

        _.refresh_data_subscriptions();

    } else {

        // Create a new record in cache and return it
        // Todo: change how generated class files work to include scope as a paramater to make it more like Model_Relationship
        row_new._scope = scope;
        let record = new model_class(row_new);

        _cache[cache_key] = record; // TODO: This should update existing records and call subscriptions.

        _.refresh_data_subscriptions();
    }
}



function _get_cache_key(model, id) {
    return JSON.stringify([model, id]);
}


function _get_request_key(model, id, revision, scope) {
    return JSON.stringify([model, id, revision, scope]);
}
















// Master list of all 'global' callbacks we are tracking.
var model_subscribe_all_callbacks = [];





// ORM object representing a record in the system.  Only one instance of an object will be defined
// at a time, and it subscribes to updates as soon as it is loaded.
_.Model_Record = class Model_Record {
    _model;
    _subscriptions;
    _ws_watched;     // Set to true when we have submitted a watch request to the server.  All records set to false when ws connection error.
    _scope;

    constructor(model_name, data) {
        // console.warn(data);
        this._subscriptions = [];
        this._model = model_name;
        this._scope = data._scope;
        delete data._scope;
        this._populate(data);
        this._ws_watched = false;
    }

    // Internal function to set / replace all properties on the record
    _populate(data) {

        for (let k in data) {
            // Is it an object that already exists?  if so, just update the id list, don't clear loaded records.
            if (typeof (this[k]) == 'object' && typeof (data[k]) == 'object' && data[k] !== null && data[k]._target_data) {
                this[k]._target_data = data[k]._target_data;
            }
            this[k] = data[k];
        }

        // We are now going to watch for updates to this record on websocket.
        this._ws_watch();
    }

    // Gets the session authenticated version of this record.  All child relationship records will be also retrieved as private.
    async private() {
        if (this._scope == 'private') {
            return this;
        } else {
            return await __get_record({ model: this._model, id: this.id, revision: this._revision, scope: "private" });
        }
    }

    // Gets the public version of this record.  All child relationship records will be also retrieved as public.
    async public() {
        if (this._scope == 'public') {
            return this;
        } else {
            return await __get_record({ model: this._model, id: this.id, revision: this._revision, scope: "public" });
        }
    }

    // Gets an updated copy of the record from the server (which also repopulates it)
    async refresh() {
        return await __get_record({ model: this._model, id: this.id, revision: this._revision, scope: this._scope }, true);
    }

    /**
     * Fetches the value at the given path within this Model_Record.
     * Use 'self' to fetch the record directly.
     * The path can specify nested properties by separating them with dots.
     * For example, "property.subproperty" fetches the "subproperty" of the "property".
     * If a property is an instance of _.Model_Relationship, the fetch() method is called and awaited.
     * If the fetch() method returns another Model_Record, fetch_path continues with the next property in the path.
     * If the fetch() method returns a value that is not a Model_Record, that value is returned.
     * Array indices can be fetched by including them in the path in the format "property[n]", where n is the index.
     * If the indexed property is a Model_Record, fetch_path continues with the next property in the path.
     * If the indexed property is not a Model_Record, it is returned.
     * If a property does not exist or does not have a fetch method, it is returned as is.
     * @param {string} path - The path to the property to fetch, separated by dots.
     * @returns {Promise} - Resolves with the value at the given path, or undefined if the path does not exist.
     */
    async fetch_path(path) {
        if (path == 'self' || path == '' || path === true) {
            return this;
        }

        let pathParts = path.split('.');
        let currentPart = pathParts.shift();

        // Handle array indices
        let arrayIndexMatch = currentPart.match(/(\w+)\[(\d+)\]/);
        if (arrayIndexMatch) {
            let propName = arrayIndexMatch[1];
            let index = parseInt(arrayIndexMatch[2]);

            // If the property is a relation object
            if (this[propName] && typeof this[propName].fetch === 'function') {
                let arrayData = await this[propName].fetch();
                let record = arrayData[index];

                if (pathParts.length > 0 && record instanceof Model_Record) {
                    return record.fetch_path(pathParts.join('.'));
                } else {
                    return record;
                }
            }
            // If the property is a simple array
            else if (Array.isArray(this[propName])) {
                let value = this[propName][index];

                if (value instanceof Model_Record) {
                    return pathParts.length > 0 ? value.fetch_path(pathParts.join('.')) : value;
                } else {
                    return value;
                }
            }
        }

        // Handle object properties
        if (this[currentPart] && typeof this[currentPart].fetch === 'function') {
            let record = await this[currentPart].fetch();
            if (record instanceof Model_Record) {
                return pathParts.length > 0 ? record.fetch_path(pathParts.join('.')) : record;
            } else {
                return record;
            }
        }

        // Handle simple properties
        return this[currentPart];
    }

    _sub_id_counter;

    // Sets up a subscription on a piece of data at a path relative to this record using fetch_path().
    // Use 'self' to subscribe to the record directly.  Returns a object with {unsubscribe()} which can
    // be called to unregister the subscription.

    // depth is equal to how many more levels down we consider properties of data.  There will be
    // circular dependencies and while the function will avoid these circular dependencies when calculating
    // a signature representing what data has changed, it is best to not go deeper than necessary.
    async subscribe(path, callback, depth = 2, do_callback_immediatly = true) {
        let that = this;

        if (!that._sub_id_counter) {
            that._sub_id_counter = 0;
        }

        that._sub_id_counter++;

        let this_subscription_id = that._sub_id_counter;

        // Get initial data, so it is all populated.
        let data = await that.fetch_path(path);
        // console.log(path);
        // console.log(data);

        let data_signature = await _.get_signature(data, depth);

        let subscription = [path, callback, this_subscription_id, data_signature, depth];

        // Create the subscription
        that._subscriptions.push(subscription);

        // Call the callback with the data right away
        // Subsequent executions of the subscription will be wrapped in a try / catch / resume,
        // but this one will fail
        // DO NOT AWAIT HERE.  COULD CAUSE CONCURRENCY ISSUES.
        // Todo: audit we aren't double executing anywhere in the code because of this.
        if (do_callback_immediatly) {
            callback(data);
        }

        // console.log("Subscription established to ", that._model, path, data)

        // Reurn a method to unsubscribe.
        return {
            unsubscribe: function () {
                that._unsubscribe(this_subscription_id);
            }
        };
    }

    // Call all subscription callbacks even if data wasn't updated
    async redraw() {
        return await this._on_data_updated(true);
    }

    // --- Private ---
    _unsubscribe(subscription_id) {
        // Filter out the subscription with the matching ID
        this._subscriptions = this._subscriptions.filter(subscription => subscription[2] !== subscription_id);
    }

    // Executes all subscribe callbacks.  Automatically called by debounce whenever any record is
    // updated on every model record.
    //
    // Even though this is async and uses await, all the data should be cached and resolve instantly,
    // and all callbacks will be called in parallel.  But just for safety we go through extra work
    // to make sure we don't rely on indexes to specify what items we have operated on, we use a list
    // of what we have handled and loop until we have handled everything.
    async _on_data_updated(force = false) {
        let that = this;

        let handled_subscriptions = [];
        let handled_everything;

        do {
            handled_everything = true;

            for (let i in that._subscriptions) {
                let path = that._subscriptions[i][0];
                let callback = that._subscriptions[i][1];
                let subscription_id = that._subscriptions[i][2];
                let old_data_signature = that._subscriptions[i][3];
                let depth = that._subscriptions[i][4];

                // if (path == "auction_registrations") {
                // console.warn(path);
                // }

                if (!handled_subscriptions.includes(subscription_id)) {
                    handled_everything = false;

                    let data = await that.fetch_path(path);

                    let new_data_signature = await _.get_signature(data, depth);

                    // console.log(path);
                    // console.log(new_data_signature);

                    if (old_data_signature !== new_data_signature || force) {
                        let found_sub = that._subscriptions.findIndex(sub => sub[2] === subscription_id);
                        if (found_sub !== -1) {
                            that._subscriptions[found_sub][3] = new_data_signature;

                            try {
                                callback(data);
                            } catch (e) {
                                console.error("Caught exception processing subscribed data callback:", e);
                            }
                        }
                    }

                    handled_subscriptions.push(subscription_id);
                    break;
                }
            }
        } while (!handled_everything);
    }

    // Used to calculate if a subscription has been updated with new data.
    // do JSON.stringify on the resulting string to get a value which can be compared to see if any of the
    // records on the given object or its children have been updated.
    async _get_record_signature(depth = 4, _object_pile = null) {
        if (_object_pile === null) {
            _object_pile = new Set();
        }

        if (depth <= 0) {
            return _object_pile;
        }

        let this_signature = JSON.stringify([this._model, this.id, this._revision]);

        if (!_object_pile.has(this_signature)) {
            _object_pile.add(this_signature);

            for (let property in this) {
                if (typeof (property) != "string") {
                    continue;
                }
                if (property.substring(0, 1) == '_') {
                    continue;

                }
                if (typeof this[property] === 'object' && this[property] !== null && this[property]._get_record_signature) {
                    await this[property]._get_record_signature(depth - 1, _object_pile);
                }
                if (Array.isArray(this[property])) {
                    return JSON.stringify(this[property]);
                    // for (let item of this[property]) {
                    //     if (typeof item === 'object' && item !== null && item._get_record_signature) {
                    //         await item._get_record_signature(depth - 1, _object_pile);
                    //     }
                    // }
                }
            }
        }

        return _object_pile;
    }

    // Returns a string that can be used to compare if data has changed on the record.
    async hash(deep = false) {
        if (deep) {
            return await this._get_record_signature();
        } else {
            return JSON.stringify([this._model, this.id, this._revision]);
        }
    }


    // Tells the web server that we are monitoring this record, and our last known revision.
    // If the server disagrees, it will send back a model_update message which will cause the client
    // to verify with ajax via .refresh().
    _ws_watch() {
        // To be clear, the series of events here is:
        // - Browser gets record from server
        // - Browser asks WS / WS returns its last known revision number, causing a subscription to
        //   future updates
        // - If WS last known revision was unknown or disagrees, we execute a refresh, passing last known
        //   revision by ajax.  Web server will only respond with new data if the revision has changed.
        if (!this._ws_watched) {
            // console.log("model_subscribe", { model: this._model, id: this.id, revision: this._revision });
            _.websocket_send_subscribe("model_subscribe", { model: this._model, id: this.id, revision: this._revision });
            this._ws_watched = true;
        }
    }
}

// Represents a set of ids which are related records to a Model_Record.  Calling fetch() retrieves these records and returns
// them.  fetch() caches its results.  If the relationship is a single record, the programmer can access property .id without
// calling fetch.
_.Model_Relationship = class Model_Relationship {
    _parent_model;
    _parent_id;
    _parent_column;
    _target_model;
    _target_relation_type;
    _target_data;
    _scope;

    constructor(parent_model, parent_id, parent_column, target_model, relation_type, target_data, scope) {
        this._parent_model = parent_model;
        this._parent_id = parent_id;
        this._parent_column = parent_column;
        this._target_model = target_model;
        this._target_relation_type = relation_type;
        this._target_data = target_data;
        this._scope = scope;

        this.length = Array.isArray(this._target_data) ? this._target_data.length : 1;

        if (!this._target_data) {
            this._target_data = []
        }

        // Set property id if it is a single record, for easy access
        if (relation_type == 'one') {
            this.id = this._target_data[0];
        }
    }

    get_model() {
        return _.get_model_class_by_name(_target_model);
    }

    list() {
        if (this._target_relation_type != 'many') {
            console.error("list() should only be called on one to many relationships");
        }

        let ids = []

        for (let d of this._target_data) {
            ids.push(d[0]);
        }

        return ids;
    }

    id() {
        if (this._target_relation_type != 'one') {
            console.error("id() should only be called on one to one relationships");
        }

        if (this._target_data && this._target_data[0] && this._target_data[0][0]) {
            return this._target_data[0][0];
        } else {
            return null;
        }
    }


    // Load and return all records from the server.  If force() is passed, all data is refreshed.
    // This is able to handle being called multiple times in parallel.
    async fetch(force = false, scope = null) {
        if (scope === null) {
            scope = this._scope;
        }

        if (this._target_relation_type == 'one' && !this._target_data[0]) {
            return null;
        }

        // Fetch missing data

        let p_results = [];

        // console.log(this._target_data);

        // Check to see if the target_data looks like this:
        // [233, 135] (id, revision)
        // Or this:
        // [[233, 135], [322, 167]] (set of [ids, revisions])
        // Or [] or null:
        // null value, return nothing

        if (this._target_data === null) {
            return null;
        }

        if (this._target_data.length == 0) {
            return [];
        }

        if (this._target_relation_type == 'one') {
            if (Array.isArray(this._target_data[0])) {
                _.shouldnt_happen();
            }

            p_results.push(_.get_record(this._target_model, this._target_data[0], this._target_data[1], scope, force));
        } else {
            if (!Array.isArray(this._target_data[0])) {
                _.shouldnt_happen();
            }

            for (let d of this._target_data) {
                p_results.push(_.get_record(this._target_model, d[0], d[1], scope, force));
            }
        }

        // Wait for all fetches to complete
        let records = await Promise.all(p_results);

        // Handle returning 'one' relationships
        if (this._target_relation_type == 'one') {
            if (!records[0]) {
                return null;
            } else {
                return records[0];
            }
        }

        // Otherwise return the whole result array - remove removed / inaccessible items
        return records.filter(item => item !== null);
    }

    async fetch_public() {
        return await this.fetch(false, 'public');
    }

    async fetch_private() {
        return await this.fetch(false, 'private');
    }

    // Like fetch, but looks for a record by id in the result set and returns it.
    // This is more efficient than accessing the record through the global model interface because
    // it allows caching
    async find(id) {
        let is_valid_rev = false;

        if (this._target_data) {
            if (!Array.isArray(this._target_data[0])) {
                if (this._target_data[0] == id) {
                    is_valid_rev = this._target_data[1];
                }
            } else {
                for (let d of this._target_data) {
                    if (d[0] == id) {
                        is_valid_rev = d[1];
                    }
                }
            }
        }

        if (!is_valid_rev) {
            return null;
        }

        return _.get_record(this._target_model, id, is_valid_rev, this._scope);
    }

    // Gets a recordset, iterates and executes callback on all of them.
    // Todo: We need a read advisory lock preventing subscibe realtime updates while this is iterating
    async each(callback) {
        let results = await this._fetch();

        for (let r of results) {
            await callback(r);
        }
    }

    // Alias for fetch
    async get(id) {
        return await this.fetch(id);
    }

    // Returns a relationship scoped to public for the same data of this function, used
    // for fast calculation of if a model has changed.
    _public() {
        if (this._scope == 'public') {
            return this;
        } else {
            return new _.Model_Relationship(
                this._parent_model,
                this._parent_id,
                this._parent_column,
                this._target_model,
                this._target_relation_type,
                this._target_data,
                'public'
            )
        }
    }

    // Used to calculate if a subscription has been updated with new data.
    // See Model_Record._get_record_signature().
    async _get_record_signature(depth = 4, _object_pile = null) {
        if (_object_pile === null) {
            _object_pile = new Set();
        }

        if (depth <= 0) {
            return _object_pile;
        }

        let rs = await this._public().fetch();

        if (rs) {
            if (Array.isArray(rs)) {
                for (var i in rs) {
                    if (rs[i]._get_record_signature) {
                        await rs[i]._get_record_signature(depth - 1, _object_pile);
                    }
                }
            } else if (rs._get_record_signature) {
                await rs._get_record_signature(depth - 1, _object_pile);
            }
        }

        return _object_pile;
    }
}

// Subscribes to all changes to the specified model class, calls callback immediatly
_.model_subscribe = function (model, callback) {
    let found = false;
    for (let i in model_subscribe_all_callbacks) {
        if (model_subscribe_all_callbacks[i].model == model) {
            found = model_subscribe_all_callbacks[i];
        }
    }

    if (found === false) {

        // Setup a debounce function so we only execute the callback once if there are multiple simultaneous updates
        let notifier = _.debounce(50, function () {
            for (let i in model_subscribe_all_callbacks) {
                if (model_subscribe_all_callbacks[i].model == model) {
                    for (let j in model_subscribe_all_callbacks[i].callbacks) {
                        model_subscribe_all_callbacks[i].callbacks[j]();
                    }
                }
            }
        });

        model_subscribe_all_callbacks.push({
            model: model,
            notifier: notifier,
            callbacks: [callback]
        });

        // Create subscription on WS server
        _.websocket_send_subscribe("model_subscribe_all", { model: model });

    } else {

        found.callbacks.push(callback);
    }

    // Call callback immediatly
    callback();
}



// Utility function to convert an arbritrary variable with objects, arrays, and records to a string hash for comparison
_.get_signature = async function (data, depth = 4, _current_path = '', _object_pile = null) {
    if (depth <= 0) {
        return;
    }

    if (_object_pile === null) {
        _object_pile = new Set();
    }

    if (data === null || typeof (data) == 'undefined') {
        // Data is null
        _object_pile.add(_current_path + "=NULL");
    } else if (data._get_record_signature && data._model && data.id) {
        // Data is a record
        _object_pile.add(_current_path + "=" + String(data._model) + ":" + data.id);
        await data._get_record_signature(depth, _object_pile);
    } else if (data._get_record_signature) {
        // Data is a relationship. This is odd but we will account for it anyway
        await data._get_record_signature(depth, _object_pile);
    } else if (Array.isArray(data)) {
        // Data is an array
        for (let i = 0; i < data.length; i++) {
            await _.get_signature(data[i], depth - 1, _current_path + "." + i, _object_pile);
        }
    } else if (typeof data === "object" && data !== null) {
        // Data is an object
        for (let [key, value] of Object.entries(data)) {
            await _.get_signature(value, depth - 1, _current_path + "." + key, _object_pile);
        }
    } else {
        // Data is a simple value
        _object_pile.add(_current_path + "=" + String(data));
    }

    // Turn into a final string if this is the top level of the recursive function stack
    if (_current_path == '') {
        let final_hash = JSON.stringify(Array.from(_object_pile));
        // console.log(final_hash);
        return final_hash;
    }
}


// // Utility function to convert an arbritrary variable with objects, arrays, and records to a string hash for comparison
// function _get_all_subscribable(data, depth = 4, _object_pile = null) {
//     if (depth <= 0) {
//         return;
//     }

//     if (_object_pile === null) {
//         _object_pile = new Set();
//     }

//     if (data._get_record_signature) {
//         _object_pile.add(data);
//     } else if (Array.isArray(data) || (typeof data !== "undefined" && typeof data.forEach !== "undefined")) {
//         // Data is an iterator
//         for (let i in data) {
//             // Iterate and get signature for each property
//             _get_all_subscribable(data[i], depth - 1, _object_pile);
//         }
//     }

//     return Array.from(_object_pile);
// }


// ============================================================================
// WEBSOCKET MESSAGE QUEUING
// ============================================================================
//
// Model updates go to a separate queue and are batched together via debounce.
// All other messages go directly to the jQuery event queue.

let _model_update_queue = [];

// Process a single model_update message - returns a promise for the get_record call
function _process_single_model_update(data) {
    let cache_key = _get_cache_key(data.model, data.id);
    let promises = [];

    for (let _cache of [model_cache_public, model_cache_private]) {
        if (_cache[cache_key]) {
            if (_cache[cache_key]._revision !== data.revision) {
                // Inline ORM data from the WS server is used directly (no HTTP fetch). A public payload
                // (orm_scope 'public', what anonymous visitors see) only replaces records held in public
                // scope; a logged-in bidder's private view of the record is fetched as before.
                if (data.orm && (data.orm_scope !== 'public' || _cache[cache_key]._scope === 'public')) {
                    promises.push(__get_record({ model: data.model, id: data.id, revision: data.revision, scope: _cache[cache_key]._scope, websocket_public_record: data.orm }));
                } else {
                    promises.push(__get_record({ model: data.model, id: data.id, revision: data.revision, scope: _cache[cache_key]._scope }));
                }
            }
        }
    }

    // A public record we do not hold yet (the server sends new bids to clients watching their
    // round): cache it now, so round.bids finds it without a fetch.
    if (data.orm && data.orm_scope === 'public' && !model_cache_public[cache_key]) {
        promises.push(__get_record({ model: data.model, id: data.id, revision: data.revision, scope: 'public', websocket_public_record: data.orm }));
    }

    return promises;
}

// Debounced function that enqueues the batch processor to the jQuery event queue
let _schedule_model_update_flush = _.debounce(100, function () {
    if (_model_update_queue.length === 0) {
        return;
    }

    // Enqueue a single event to the jQuery event queue that processes all pending model updates
    $.eventQueue.push(async function () {
        // Drain the queue
        let updates_to_process = _model_update_queue;
        _model_update_queue = [];

        // Deduplicate: keep highest revision for each model:id
        let deduped = {};
        for (let update of updates_to_process) {
            let key = update.model + ':' + update.id;
            if (!deduped[key] || update.revision > deduped[key].revision) {
                deduped[key] = update;
            }
        }

        let unique_updates = Object.values(deduped);

        // Process all model updates, collecting promises
        let all_promises = [];
        for (let update of unique_updates) {
            let promises = _process_single_model_update(update);
            all_promises.push(...promises);
        }

        // Wait for all get_record calls to complete
        if (all_promises.length > 0) {
            // One failed fetch must not stop the others or the subscription refresh below
            let results = await Promise.allSettled(all_promises);
            for (let r of results) {
                if (r.status === 'rejected') {
                    console.error('Realtime record refresh failed', r.reason);
                }
            }
        }

        // Now refresh all subscriptions and await their callbacks
        await _refresh_data_subscriptions_immediate();
    });
});

// Immediate version of refresh_data_subscriptions for use within batch processor
async function _refresh_data_subscriptions_immediate() {
    for (let _cache of [model_cache_public, model_cache_private]) {
        for (let j in _cache) {
            if (_cache[j] && _cache[j]._on_data_updated) {
                await _cache[j]._on_data_updated();
            }
        }
    }
}

// WebSocket message handler - must be synchronous, queues work to event queue
_.on_websocket_receive(function (topic, data) {

    if (topic == "model_update") {
        // Validate
        if (!data.hasOwnProperty('model') || !data.hasOwnProperty('id') || !data.hasOwnProperty('revision')) {
            console.error("WS: Invalid data from server.  model_update should have model, id, revision.", data);
            return;
        }

        // Add to model update queue
        _model_update_queue.push(data);

        // Schedule the debounced flush
        _schedule_model_update_flush();
        return;
    }

    if (topic == "model_subscribe_all_update") {
        // Queue this as a regular event
        $.eventQueue.push(async function () {
            if (!data.hasOwnProperty('model')) {
                console.error("WS: Invalid data from server.  model_subscribe_all_update should have model", data);
                return;
            }
            for (let i in model_subscribe_all_callbacks) {
                if (model_subscribe_all_callbacks[i].model == data.model) {
                    await model_subscribe_all_callbacks[i].notifier();
                }
            }
        });
        return;
    }

    // All other topics: queue directly to event queue with no special handling
    // Currently no other topics need processing here, but this is the pattern for future ones
});


// Called whenever any record globally is updated via WS realtime updates.
// Recalculates all subscribed data and calls back render functions on anything
// that has changed.

_.refresh_data_subscriptions = _.debounce(0, async function () {
    for (let _cache of [model_cache_public, model_cache_private]) {
        for (let j in _cache) {
            if (_cache[j] && _cache[j]._on_data_updated) {
                await _cache[j]._on_data_updated();
            }
        }
    }
});
